import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { PatientProfileInfoAccordionEditable } from './PatientProfileInfoAccordion';
import { ShowMoreContent } from './ShowMoreContent.component';
import { HeightAndWeightForm } from './HeightAndWeightForm.component';
import { getFeetAndInchesFromHeightInches, useAlert } from './../../../..';
import { usePatientMeasurementsQuery } from '@uplift/queries';
import { setPatientMeasurement } from '@uplift/api';
import * as Yup from 'yup';

export type HeightAndWeightAccordianProps = {
  patientId: string;
  editable: boolean;
  inlineForm?: boolean;
};

export type HeightAndWeightFormType = {
  height?: number | null;
  weight: number | null;
};

const heightAndWeightValidation = (): Yup.ObjectSchema<HeightAndWeightFormType> => {
  return Yup.object({
    height: Yup.number().nullable(),
    weight: Yup.number().nullable().min(1, 'Invalid weight').max(999, 'Invalid weight').required('Weight is required'),
  });
};

export const HeightAndWeightAccordian: React.VFC<HeightAndWeightAccordianProps> = ({ patientId, editable, inlineForm }) => {
  const alert = useAlert();
  const { data: measurementsData = [], refetch } = usePatientMeasurementsQuery(patientId);

  const measurements = [ ...measurementsData ].sort((a, b) => CalendarDateService.compare(b.createdAt, a.createdAt));

  const currentMeasurement = measurements.find(m => !m.archivedAt);

  const initialValues: HeightAndWeightFormType = {
    height: currentMeasurement?.heightInches ?? null,
    weight: currentMeasurement?.weightLbs ?? null,
  };

  const onSubmit = async (values: HeightAndWeightFormType) => {
    await setPatientMeasurement(patientId, {
      heightInches: values.height || undefined,
      weightLbs: values.weight || undefined
    });

    await refetch();
  };

  const content = useMemo(() => {
    if (!measurements?.length) {
      return <Typography variant="body2" color="textPrimary">None on record</Typography>;
    }

    return (
      <ShowMoreContent
        canShowMore={measurements.length > 1}
        buttonText={(showingMore) => showingMore ? 'Hide History' : 'Show History'}
      >
        {(showingMore) => measurements.slice(0, showingMore ? undefined : 1).map(({ heightInches, weightLbs, createdAt }, i) => {
          const { feet = 0, inches = 0 } = getFeetAndInchesFromHeightInches(heightInches);

          const heightDisplay = heightInches ? `${feet}'${inches}"` : null;
          const weightDisplay = weightLbs ? `${weightLbs} lbs` : null;

          if (!heightDisplay && !weightDisplay) {
            return null;
          }

          const heightAndWeightDisplay = [ heightDisplay, weightDisplay ].filter(x => x).join(', ');

          return (
            <Box display="flex" alignItems="center" gap="8px" width="100%" key={i}>
              <Box fontWeight={500}>
                <Typography variant="body2" color="textPrimary">{heightAndWeightDisplay}</Typography>
              </Box>
              <Typography variant="body2">updated {CalendarDateService.date(createdAt).format(DateFormats['M/D/YY'])}</Typography>
            </Box>
          );
        })}
      </ShowMoreContent>
    );
  }, [ measurements ]);

  return (
    <PatientProfileInfoAccordionEditable
      title="Height and Weight"
      editable={editable}
      inlineForm={inlineForm}
      formikConfig={{
        initialValues,
        onSubmit,
        validationSchema: heightAndWeightValidation
      }}
      form={<HeightAndWeightForm />}
      onSuccess={(onCancelEdit) => {
        alert.success('Height and weight updated');
        onCancelEdit();
      }}
    >
      {content}
    </PatientProfileInfoAccordionEditable>
  );
};
